/**
 * Colour helpers for the renderer. Strings built here are cached, so drawing code can ask
 * for `rgba(color, 0.4)` every frame without allocating.
 */

const rgbaCache = new Map<string, Map<number, string>>();

/** Parses `#rrggbb` (or `#rgb`) into [r, g, b]; unknown formats give white. */
export function parseHex(hex: string): [number, number, number] {
  let body = hex.startsWith('#') ? hex.slice(1) : hex;
  if (body.length === 3) body = [...body].map((c) => c + c).join('');
  const value = Number.parseInt(body, 16);
  if (body.length !== 6 || Number.isNaN(value)) return [255, 255, 255];
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex(r: number, g: number, b: number): string {
  const part = (v: number) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** `#rrggbb` with an alpha as a CSS colour. Alpha is quantized to 1/100 (and cached). */
export function rgba(hex: string, alpha: number): string {
  const key = Math.round(Math.min(1, Math.max(0, alpha)) * 100);
  let byAlpha = rgbaCache.get(hex);
  if (!byAlpha) {
    byAlpha = new Map();
    rgbaCache.set(hex, byAlpha);
  }
  let color = byAlpha.get(key);
  if (!color) {
    const [r, g, b] = parseHex(hex);
    color = `rgba(${r}, ${g}, ${b}, ${key / 100})`;
    byAlpha.set(key, color);
  }
  return color;
}

/** WCAG relative luminance of a hex colour: 0 (black) .. 1 (white). */
export function luminance(hex: string): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = parseHex(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Linear mix of two hex colours: t = 0 gives `a`, 1 gives `b`. */
export function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  return toHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** Lighter (amount > 0, towards white) or darker (amount < 0, towards black). */
export function shade(hex: string, amount: number): string {
  return amount >= 0 ? mix(hex, '#ffffff', amount) : mix(hex, '#000000', -amount);
}
